import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CircleIcon, BrainCogIcon, NetworkIcon, ShieldIcon } from 'lucide-react';
export const Services: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['10%', '-10%']);
  const titleY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const services = [{
    title: 'AI Strategy',
    description: 'Custom roadmaps for AI implementation aligned with your business objectives and industry position.',
    icon: <BrainCogIcon className="w-8 h-8 text-neon-blue" data-id="element-132" />
  }, {
    title: 'Industry Tailored Agents',
    description: 'Bespoke AI agents designed for your specific use cases, data, and performance requirements.',
    icon: <CircleIcon className="w-8 h-8 text-neon-blue" data-id="element-133" />
  }, {
    title: 'Integration',
    description: 'Seamless deployment of AI systems into your existing technology infrastructure and workflows.',
    icon: <NetworkIcon className="w-8 h-8 text-neon-blue" data-id="element-134" />
  }, {
    title: 'Security & Compliance',
    description: 'Enterprise-grade security protocols and regulatory compliance for your AI deployments.',
    icon: <ShieldIcon className="w-8 h-8 text-neon-blue" data-id="element-135" />
  }];
  return <section id="services" ref={sectionRef} className="relative py-24 md:py-32 bg-gradient-to-b from-black to-cosmic-blue/30 overflow-hidden" data-id="element-136">
      <div className="container mx-auto px-4 md:px-6" data-id="element-137">
        <motion.div className="text-center mb-16" style={{
        y: titleY,
        opacity: titleOpacity
      }} data-id="element-138">
          <motion.h2 className="text-3xl md:text-4xl font-heading font-light tracking-wider mb-4 text-neon-blue glow-text" initial={{
          scale: 0.8,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-139">
            Our Services
          </motion.h2>
          <motion.p className="text-neon-blue/80 max-w-2xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-140">
            Comprehensive AI solutions designed to transform your business with
            precision and expertise.
          </motion.p>
        </motion.div>
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{
        y
      }} data-id="element-141">
          {services.map((service, index) => <motion.div key={index} className="service-card p-6 rounded-sm h-full flex flex-col" initial={{
          opacity: 0,
          y: 50,
          rotateX: -15
        }} whileInView={{
          opacity: 1,
          y: 0,
          rotateX: 0
        }} transition={{
          duration: 0.7,
          delay: index * 0.15,
          ease: 'easeOut'
        }} viewport={{
          once: true,
          amount: 0.3
        }} whileHover={{
          y: -15,
          scale: 1.03,
          transition: {
            duration: 0.3
          }
        }} data-id="element-142">
              <motion.div className="mb-4 relative" initial={{
            scale: 0,
            rotate: -180
          }} whileInView={{
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 0.6,
            delay: index * 0.15 + 0.3,
            type: 'spring',
            stiffness: 200
          }} viewport={{
            once: true
          }} data-id="element-143">
                <div className="absolute -inset-1 bg-gradient-to-r from-neon-blue/20 to-cosmic-blue/40 rounded-full blur opacity-30" data-id="element-144" />
                <div className="relative bg-black/50 p-3 rounded-full inline-block" data-id="element-145">
                  {service.icon}
                </div>
              </motion.div>
              <h3 className="text-xl font-heading font-light mb-3 text-neon-blue" data-id="element-146">
                {service.title}
              </h3>
              <p className="text-neon-blue/70 text-sm leading-relaxed flex-grow" data-id="element-147">
                {service.description}
              </p>
              <motion.a href="#" className="mt-4 inline-flex items-center text-sm text-neon-blue/90 hover:text-neon-blue group" whileHover={{
            x: 5
          }} transition={{
            duration: 0.2
          }} data-id="element-148">
                Learn more
                <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-149">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" data-id="element-150"></path>
                </svg>
              </motion.a>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
};